import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

import EntityFormPage, { EntityForm } from '@entity/EntityForm/EntityForm';
import getGraphqlData from '@boilerplate/lib/getGraphqlData';
import { BaseFormUpdateProps } from '@boilerplate/types/entity';
import { addEntityToRelatedDataOptions } from '@boilerplate/lib/relatedDataToOptions';
import Notistack from '@/lib/notistack';
import ContractEntity from '@/entities/contract';
import { catchGraphQlError } from '@/lib/catchError';

  import UserEntity from '@/entities/user';
  import TenantEntity from '@/entities/tenant';
  import ProjectEntity from '@/entities/project';

function ContractBaseUpdateForm({ formOnly = false, onClose, Entity = ContractEntity, id: propId, ...props }: BaseFormUpdateProps<typeof ContractEntity.model>) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const id = propId || paramId;

  const Component = formOnly ? EntityForm : EntityFormPage;

  const { loading, data } = ContractEntity.model.useGet({
    variables: {
      id,
            withUser: true,
            withTenant: true,
            withProject: true,
    },
  });

  const [updateMutation, { loading: updateLoading }] = ContractEntity.model.useUpdate();

  const relationsOptionsQuery = ContractEntity.model.useRelationsOptions({
    variables: {
          withUsers: true,
          withTenants: true,
          withProjects: true,
    }
  });

  const relationsOptions = useMemo(
    () =>
      addEntityToRelatedDataOptions(relationsOptionsQuery, [
              UserEntity,
              TenantEntity,
              ProjectEntity,
      ]),
    [relationsOptionsQuery]
  );

  const handleSubmit = (newData) => {
    newData.variables = getGraphqlData(Entity, newData.variables, 'update');

    updateMutation(newData)
      .then((result) => {
        Notistack.toast(t('crud:updatedItem', { item: t('entities:contract') }), { variant: 'success' });

        if (!formOnly) {
          navigate(-1);
        }

        return onClose?.(result.data?.updateContract);
      })
      .catch(catchGraphQlError);
  };

  if (loading || relationsOptionsQuery.loading) {
    return <CircularProgress />;
  }

  return (
    <Component
      {...props}
      type="update"
      Entity={Entity}
      loading={updateLoading}
      handleSubmit={handleSubmit}
      relationsOptions={relationsOptions}
      data={data.contract}
    />
  );
}

export default ContractBaseUpdateForm;
